import { useMemo, useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  PieChart, 
  AlertTriangle,
  CheckCircle,
  Rocket,
  RefreshCw,
  ArrowRight,
  ArrowUpRight,
} from 'lucide-react';
import ActivePlansWidget from './ActivePlansWidget';
import TopMovers from './TopMovers';
import VelocityAlerts from './VelocityAlerts';
import AttentionRequired from './AttentionRequired';
import EarningsCalendar from './EarningsCalendar';
import { fetchApi } from '../api/client';

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function StatCard({ icon: Icon, label, value, subValue, trend, color = 'sky' }) {
  const colorClasses = {
    sky: 'from-sky-500/20 to-sky-600/10 border-sky-500/30',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
    violet: 'from-violet-500/20 to-violet-600/10 border-violet-500/30',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30',
  };

  const iconColors = {
    sky: 'text-sky-400',
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    violet: 'text-violet-400',
    red: 'text-red-400',
  };

  return (
    <div className={`stat-card bg-gradient-to-br ${colorClasses[color]} border`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="stat-label">{label}</p>
          <p className="stat-value mt-1">{value}</p>
          {subValue && (
            <p className={`text-sm mt-1 ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-slate-400'}`}>
              {trend === 'up' && <TrendingUp className="w-3 h-3 inline mr-1" />}
              {trend === 'down' && <TrendingDown className="w-3 h-3 inline mr-1" />}
              {subValue}
            </p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function DashboardDeploy({ onNavigate }) {
  const [eligible, setEligible] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApi('/deployment-plans/eligible')
      .then(data => setEligible(data))
      .catch(err => console.error('Deploy fetch error:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card">
        <h3 className="card-header">
          <Rocket className="w-5 h-5 text-sky-400" />
          Deploy
        </h3>
        <div className="flex justify-center py-8">
          <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  const tranches = eligible?.eligible_tranches || [];
  const frozenCount = (eligible?.frozen_tranches || []).length;
  const alertCount = (eligible?.acceleration_alerts || []).length;

  return (
    <div className="card">
      <h3 className="card-header">
        <Rocket className="w-5 h-5 text-sky-400" />
        Deploy
        {tranches.length > 0 && (
          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
            {tranches.length} eligible
          </span>
        )}
      </h3>

      {tranches.length === 0 ? (
        <p className="text-sm text-slate-400">No eligible tranches today</p>
      ) : (
        <div className="space-y-2">
          {tranches.slice(0, 5).map(t => (
            <div key={t.tranche_id} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30 border-l-2 border-emerald-500/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center text-sm font-bold text-sky-400">
                  {t.ticker.slice(0, 2)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-200">{t.ticker}</span>
                    <span className="text-xs text-slate-500 capitalize">{t.direction}</span>
                    <span className="text-xs text-slate-500">T{t.tranche_number}</span>
                  </div>
                  <p className="text-xs text-slate-400 font-mono">
                    ${Number(t.dollar_amount || 0).toLocaleString()}
                    {t.current_price && t.price_ceiling && (
                      <span className={t.price_vs_ceiling === 'below' ? ' text-emerald-400' : ' text-red-400'}>
                        {' · '}${Number(t.current_price).toFixed(2)} vs ≤${Number(t.price_ceiling).toFixed(2)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-emerald-400" />
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
        {frozenCount > 0 && <span>{frozenCount} frozen</span>}
        {alertCount > 0 && <span className="text-red-400">{alertCount} acceleration alert{alertCount > 1 ? 's' : ''}</span>}
        <button
          onClick={() => onNavigate('deploy')}
          className="text-sky-400 hover:text-sky-300 transition-colors flex items-center gap-1 ml-auto"
        >
          View all <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function Dashboard({ summary, onNavigate }) {
  const [performanceData, setPerformanceData] = useState(null);

  useEffect(() => {
    fetchApi('/performance/summary')
      .then(data => setPerformanceData(data))
      .catch(err => console.error('Failed to fetch performance:', err));
  }, [summary]);

  if (!summary) {
    return <div className="text-slate-400">No data available</div>;
  }

  const { 
    positions, 
    allocation, 
    rule_violations, 
    warnings = [], 
    total_value,
  } = summary;

  const unrealizedGainLoss = summary.unrealized_gain_loss ??
    positions.reduce((sum, p) => sum + (p.gain_loss_dollars || 0), 0);
  const realizedGainLoss = summary.realized_gain_loss ??
    performanceData?.realized_gains?.total_gain_loss ?? 0;
  const totalGainLoss = summary.total_gain_loss ??
    (unrealizedGainLoss + realizedGainLoss);
  
  const positionCount = positions.length;
  const accountCount = new Set(positions.map(p => p.account_id)).size;
  
  const violationCount = rule_violations.length;
  const warningCount = warnings.length;
  
  let ruleStatusValue, ruleStatusColor, ruleStatusIcon, ruleStatusSub;
  if (violationCount > 0) {
    ruleStatusValue = `${violationCount} Violation${violationCount > 1 ? 's' : ''}`;
    ruleStatusColor = 'red';
    ruleStatusIcon = AlertTriangle;
    ruleStatusSub = warningCount > 0 ? `${warningCount} warning${warningCount > 1 ? 's' : ''}` : undefined;
  } else if (warningCount > 0) {
    ruleStatusValue = `${warningCount} Warning${warningCount > 1 ? 's' : ''}`;
    ruleStatusColor = 'amber';
    ruleStatusIcon = AlertTriangle;
    ruleStatusSub = undefined;
  } else {
    ruleStatusValue = 'All Clear';
    ruleStatusColor = 'emerald';
    ruleStatusIcon = CheckCircle;
    ruleStatusSub = undefined;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* KPI stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={DollarSign}
          label="Total Portfolio Value"
          value={formatCurrency(total_value)}
          color="sky"
        />
        <StatCard
          icon={totalGainLoss >= 0 ? TrendingUp : TrendingDown}
          label="Total Gain/Loss"
          value={formatCurrency(totalGainLoss)}
          subValue={`Unrealized: ${formatCurrency(unrealizedGainLoss)} \u2022 Realized: ${formatCurrency(realizedGainLoss)}`}
          trend={totalGainLoss >= 0 ? 'up' : 'down'}
          color={totalGainLoss >= 0 ? 'emerald' : 'amber'}
        />
        <StatCard
          icon={PieChart}
          label="Positions"
          value={positionCount}
          subValue={`${accountCount} account${accountCount > 1 ? 's' : ''}`}
          color="violet"
        />
        <StatCard
          icon={ruleStatusIcon}
          label="Rule Status"
          value={ruleStatusValue}
          subValue={ruleStatusSub}
          color={ruleStatusColor}
        />
      </div>

      {/* Attention Required */}
      <AttentionRequired violations={rule_violations} warnings={warnings} />

      {/* Velocity Alerts */}
      <VelocityAlerts />

      {/* Upcoming Earnings (capped at 5) */}
      <EarningsCalendar limit={5} onViewAll={() => onNavigate('scorecards')} />

      {/* Deployment Plans */}
      <DashboardDeploy onNavigate={onNavigate} />

      {/* Top Movers (capped at 5) */}
      <TopMovers limit={5} onViewAll={() => onNavigate('positions')} />

      {/* Active Trade Plans */}
      <div className="max-w-2xl">
        <ActivePlansWidget onNavigate={onNavigate} />
      </div>
    </div>
  );
}
